import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { api } from '../api'
import type { ContentItem, HistoricalFigure, Place } from '../api/apiClient'
import FeaturedExperienceSection from '../components/homepage/FeaturedExperienceSection'
import HomepageHero from '../components/homepage/HomepageHero'
import MapPreviewSection from '../components/homepage/MapPreviewSection'
import PublicHistorySection from '../components/homepage/PublicHistorySection'
import StartJourneySection from '../components/homepage/StartJourneySection'
import SupportingDiscoveriesSection from '../components/homepage/SupportingDiscoveriesSection'
import WhyPitSection from '../components/homepage/WhyPitSection'
import { useHomepageContent } from '../hooks/useHomepageContent'
import { usePageMetadata } from '../hooks/usePageMetadata'
import { FIGURES } from '../data/figures'
import { NEWS_FALLBACK_CONTENT } from '../data/newsFallback'
import { PLACES } from '../data/places'
import { getApiBase, resolveMediaUrl } from '../utils/media'
import { toNewsCardItem, type NewsCardItem } from '../utils/content'

const FEATURED_IMAGE_FALLBACK = '/images/home/history_news.png'
const FEATURED_VOICE_ROLE = 'Queen of England, 1533-1536'
const FEATURED_VOICE_SUPPORT = 'Ask about court life, imprisonment, and royal power at the Tower.'

const accentFromEra = (primaryEra?: string): string => {
  const era = primaryEra?.toLowerCase() ?? ''
  if (era.includes('neolithic')) return 'neolithic'
  if (era.includes('roman')) return 'roman'
  if (era.includes('medieval')) return 'medieval'
  if (era.includes('renaissance') || era.includes('tudor') || era.includes('stuart')) return 'renaissance'
  return 'modern'
}

const buildLocation = (place: Place): string => {
  if (place.location_label) return place.location_label
  if (place.locationLabel) return place.locationLabel
  if (place.region || place.country) {
    return [place.region, place.country].filter(Boolean).join(', ')
  }
  return ''
}

const buildPlaceEra = (place?: Place | null): string =>
  place?.era_primary ?? place?.primaryEra ?? 'Historic site'

const mergeBySlug = <T extends { slug: string }>(primary: T[], fallback: T[]): T[] => {
  const merged = new Map<string, T>()

  fallback.forEach((item) => {
    merged.set(item.slug, item)
  })

  primary.forEach((item) => {
    const existing = merged.get(item.slug)
    merged.set(item.slug, existing ? ({ ...existing, ...item } as T) : item)
  })

  return Array.from(merged.values())
}

const normalizeValue = (value?: string | null): string => value?.trim().toLowerCase() ?? ''

const findFigureByPreference = (
  figures: HistoricalFigure[],
  preferredSlug: string,
  preferredName: string,
): HistoricalFigure | null => {
  const normalizedName = normalizeValue(preferredName)

  return (
    figures.find((figure) => {
      const figureName = normalizeValue(figure.name)
      return (
        figure.slug === preferredSlug ||
        figureName === normalizedName ||
        figureName.includes(normalizedName) ||
        normalizedName.includes(figureName)
      )
    }) ?? null
  )
}

const getFeaturedImage = (apiBase: string, place?: Place | null): string => {
  const source = place?.hero_image ?? place?.heroImage ?? place?.echo_image ?? place?.echoImage ?? FEATURED_IMAGE_FALLBACK
  return resolveMediaUrl(apiBase, source) ?? source
}

const getFeaturedFigureImage = (apiBase: string, figure?: HistoricalFigure | null): string | null => {
  const legacyFigure = (figure as Record<string, unknown> | null) ?? null
  const source =
    figure?.image_url ??
    (legacyFigure?.['profile_image_url'] as string | undefined) ??
    (legacyFigure?.['image_thumb_url'] as string | undefined) ??
    (legacyFigure?.['thumbnail_url'] as string | undefined) ??
    (legacyFigure?.['portrait_small'] as string | undefined) ??
    (legacyFigure?.['hero_image'] as string | undefined) ??
    (legacyFigure?.['heroImage'] as string | undefined) ??
    null

  if (typeof source !== 'string' || !source.trim()) return null
  return resolveMediaUrl(apiBase, source) ?? source
}

const appendPrompt = (href: string, prompt: string): string => {
  const separator = href.includes('?') ? '&' : '?'
  return `${href}${separator}prompt=${encodeURIComponent(prompt)}`
}

const Home = () => {
  const location = useLocation()
  const apiBase = getApiBase()
  const content = useHomepageContent()
  const [places, setPlaces] = useState<Place[]>([])
  const [figures, setFigures] = useState<HistoricalFigure[]>([])
  const [newsFeed, setNewsFeed] = useState<ContentItem[]>([])
  const searchParams = new URLSearchParams(location.search)
  const isPreview = searchParams.get('preview') === '1'

  useEffect(() => {
    let cancelled = false

    api
      .listPlaces()
      .then((data) => {
        if (!cancelled) {
          setPlaces(data)
        }
      })
      .catch(() => {
        // fallback content is merged below
      })

    api
      .listFigures()
      .then((data) => {
        if (!cancelled) {
          setFigures(data)
        }
      })
      .catch(() => {
        // fallback content is merged below
      })

    api
      .listNews({ limit: 3, status: 'published', sort: 'published_at', order: 'desc' })
      .then((data) => {
        if (!cancelled) {
          setNewsFeed(data)
        }
      })
      .catch(() => {
        // fallback content is merged below
      })

    return () => {
      cancelled = true
    }
  }, [])

  const activePlaces = mergeBySlug(places, PLACES as Place[])
  const activeFigures = mergeBySlug(figures, FIGURES as HistoricalFigure[])
  const featuredPlace =
    activePlaces.find((place) => place.slug === content.featuredExperience.placeSlug) ?? null
  const featuredFigure = findFigureByPreference(
    activeFigures,
    content.featuredExperience.preferredFigureSlug,
    content.featuredExperience.preferredFigureName,
  )
  const placeHref = featuredPlace ? `/places/${featuredPlace.slug}` : '/places'
  const placeContextQuery = featuredPlace ? `?place_slug=${encodeURIComponent(featuredPlace.slug)}` : ''
  const conversationHref = featuredFigure
    ? `/guest/${featuredFigure.slug}${placeContextQuery}`
    : '/people'
  const promptBaseHref = featuredFigure
    ? `/guest/${featuredFigure.slug}${placeContextQuery}`
    : `/guest/quotha${placeContextQuery}`
  const conversationLabel = featuredFigure
    ? `Talk to ${featuredFigure.name}`
    : 'Meet the Figures'
  const featuredImageSrc = getFeaturedImage(apiBase, featuredPlace)
  const featuredVoiceImageSrc = getFeaturedFigureImage(apiBase, featuredFigure)
  const promptLinks = content.featuredExperience.promptChips.map((prompt) => ({
    label: prompt,
    href: appendPrompt(promptBaseHref, prompt),
  }))
  const newsItems: NewsCardItem[] = newsFeed.length > 0
    ? newsFeed.slice(0, 3).map((item) => toNewsCardItem(item, '/news'))
    : NEWS_FALLBACK_CONTENT.slice(0, 3).map((item) => toNewsCardItem(item, '/news'))
  const latestDiscoveryTitle = newsItems[0]?.title

  usePageMetadata({
    title: 'Places in Time | Historic Places, People and AI History Chat',
    description:
      "Explore Britain's historic places, discover the people behind them, and ask questions through evidence-led AI history chat.",
    path: '/',
  })

  return (
    <>
      <HomepageHero content={content.hero} heroImage={content.heroImage} />
      <StartJourneySection content={content.startJourney} />
      <FeaturedExperienceSection
        content={content.featuredExperience}
        exploreHref={placeHref}
        conversationHref={conversationHref}
        conversationLabel={conversationLabel}
        promptLinks={promptLinks}
        visualSrc={featuredImageSrc}
        visualAlt={featuredPlace ? `View of ${featuredPlace.name}` : 'Historic image for the featured experience'}
        location={buildLocation(featuredPlace) || 'City of London, by the River Thames'}
        era={buildPlaceEra(featuredPlace)}
        eraAccent={accentFromEra(buildPlaceEra(featuredPlace))}
        voiceHref={conversationHref}
        voiceName={featuredFigure?.name ?? content.featuredExperience.preferredFigureName}
        voiceRole={FEATURED_VOICE_ROLE}
        voiceSupport={FEATURED_VOICE_SUPPORT}
        voiceImageSrc={featuredVoiceImageSrc}
      />
      <WhyPitSection content={content.whyDifferent} />
      <MapPreviewSection
        content={content.mapExplore}
        latestDiscoveryTitle={latestDiscoveryTitle}
      />
      <PublicHistorySection content={content.publicHistory} />
      <SupportingDiscoveriesSection content={content.supportingDiscoveries} items={newsItems} />

      {isPreview && (
        <div className="preview-pill" aria-live="polite">
          Preview mode - published content
        </div>
      )}
    </>
  )
}

export default Home
